import React, { useEffect, useContext, useMemo, ComponentType } from 'react'
import Box from '@mui/material/Box'
import {
	Route,
	Routes,
	useNavigate,
	Navigate,
	useLocation,
} from 'react-router-dom'
import AppContext from './context/appContext'
import cn from './lib/utils/cn'
import './App.css'
import './tailwind-setup.css'
import '@mantine/dates/styles.css'
import loadable, { LoadableComponent } from '@loadable/component'
import LockerLoader from './components/common/LockerLoader'
import CustomSnackbar from './components/common/snackBar'
import AutoLogout from './AutoLogout'
import FeatureGuard from './components/common/FeatureGuard'
import { Features, isRestrictedPlanStatus } from './context/billingContext'
import { BillingProvider } from './context/billingContext'
import AdLogin from './pages/Login'
import AuthLogin from './pages/AuthLogin'
import PostRegisterPage from './pages/PostRegister/PostRegisterPage'
import { AuthLayout, MainLayout } from './components/layouts'
import PlanBlock from './pages/PlanBlock'
import Fallback from './components/ui/Fallback'
import { useAuthSession } from './hooks'
import { isAuthExcludedPathname } from './lib/utils/authPath'
type LoadableImport<P = object> = () => Promise<{ default: ComponentType<P> }>

const load = <P,>(fn: LoadableImport<P>): LoadableComponent<P> =>
	loadable(fn, { fallback: <LockerLoader visible /> })

const AgentAuth = load(() => import('./pages/AgentAuth'))
const AdminLayout = load(() => import('./components/layouts/AdminLayout'))
const GroupsPage = load(() => import('./pages/Admin/GroupsPage'))
const UsersPage = load(() => import('./pages/Admin/UsersPage'))
const PermissionsPage = load(() => import('./pages/Admin/PermissionsPage'))
const RolesPage = load(() => import('./pages/Admin/RolesPage'))
const CategoriesPage = load(() => import('./pages/Admin/CategoriesPage'))
const AuditPage = load(() => import('./pages/Admin/AuditPage'))
const UserPage = load(() => import('./pages/Settings/UserPage'))
const OrganizationPage = load(() => import('./pages/Settings/OrganizationPage'))
const BillingPage = load(() => import('./pages/Settings/BillingPage'))
const EmailVerification = load(
	() => import('./pages/PostRegister/EmailVerification'),
)
const DomainVerified = load(() => import('./pages/PostRegister/DomainVerified'))
const DomainVerificationFailed = load(
	() => import('./pages/PostRegister/DomainVerificationFailed'),
)
const Sidebar = load(() => import('./components/common/Sidebar'))
const EmailVerified = load(() => import('./pages/PostRegister/EmailVerified'))
const InvalidLink = load(() => import('./pages/PostRegister/InvalidLink'))
const AcceptInvitePage = load(() => import('./pages/Public/AcceptInvitePage'))
const Maintenance = load(() => import('./pages/Maintenance/index'))
const HomePage = load(() => import('./pages/Workspace/HomePage'))
const RecentPage = load(() => import('./pages/Workspace/RecentPage'))
const FavoritesPage = load(() => import('./pages/Workspace/FavoritesPage'))
const TrashPage = load(() => import('./pages/Workspace/TrashPage'))
const EsignRequestsPage = load(
	() => import('./pages/Workspace/EsignRequestsPage'),
)
const FilePreviewPage = load(() => import('./pages/Workspace/FilePreviewPage'))
const SearchResultsPage = load(
	() => import('./pages/Workspace/SearchResultsPage'),
)
const PasswordSuccess = load(
	() => import('./pages/PostRegister/PasswordSuccess'),
)
const MfaChoice = load(() => import('./pages/Settings/MfaChoice'))
const NotesPage = load(() => import('./pages/MySpace/NotesPage'))
const ContactsPage = load(() => import('./pages/MySpace/ContactsPage'))
const CalendarPage = load(() => import('./pages/MySpace/CalendarPage'))
const WikiPages = load(() => import('./pages/MySpace/WikiPages'))
const BoardsListPage = load(
	() => import('./pages/MySpace/BoardsPages/BoardsListPage'),
)
const BoardPage = load(() => import('./pages/MySpace/BoardsPages/BoardPage'))

const InboxPage = load(() => import('./pages/MySpace/InboxPage'))
const SigningPage = load(
	() => import('./pages/Workspace/FileSigningPage/Signing'),
)
const PublicSignFilePage = load(
	() => import('./pages/Public/PublicSignFilePage'),
)
const PublicFileViewPage = load(
	() => import('./pages/Public/PublicFileViewPage'),
)
const UserBlocked = load(() => import('./pages/UserBlocked'))

// const personnalDomains = [
// 	'gmail.com',
// 	'yahoo.com',
// 	'hotmail.com',
// 	'outlook.com',
// 	'aol.com',
// 	'icloud.com',
// 	'zoho.com',
// 	'protonmail.com',
// 	'yandex.com',
// 	'mail.com',
// 	'gmx.com',
// 	'inbox.com',
// 	'lycos.com',
// 	'hushmail.com',
// ]

const assetsLocations = [
	'/',
	// '/aura',
	'dashboard',
	'recent',
	'favorites',
	'trash',
	'groups',
	'users',
	'roles',
	'permissions',
	'categories',
	'settings',
	'my-space',
]

const excludedSidebarPaths = [
	'plan-block',
	'domain-verified',
	'domain-verification-failed',
	'verify-email',
	'email-verified',
	'invalid-invite',
	'accept-invite',
	'invite-accepted',
	'maintenance',
	'password-change-success',
	'preview',
	'file-agent-auth',
	'mfa-choice',
	'create-profile',
	'ad-login',
	'auth0-login',
	'user-blocked',
	'file/view',
	'esign/sign',
]

const excludedAuthPaths = [
	// 'create-profile',
	'domain-verified',
	'domain-verification-failed',
	'verify-email',
	'email-verified',
	'invalid-invite',
	'accept-invite',
	'invite-accepted',
	'maintenance',
	'mfa-choice',
	'ad-login',
	'auth0-login',
	'password-change-success',
	'user-blocked',
	'file/view', // public file preview by eSign-token — no login required
	'esign/sign', // public signing by eSign-token (in the search URl params - ?token=…)
]

const App = () => {
	const { darkMode, setDarkMode } = useContext(AppContext)
	const location = useLocation()
	const navigate = useNavigate()

	const {
		currentUser,
		isAuthenticated,
		isLoading,
		isEmptyUserResponse,
		needsCreateProfileRedirect,
		shouldShowUserLoadError,
		shouldWaitForCurrentUser,
		userProfileError,
	} = useAuthSession({ excludedAuthPaths })

	useEffect(() => {
		if (currentUser?.profile?.preferences) {
			setDarkMode(currentUser.profile.preferences.theme === 'dark')
		}
	}, [currentUser?.profile?.preferences, setDarkMode])

	const isPlanRestricted = isRestrictedPlanStatus(currentUser?.plan?.status)

	// Profile checks
	useEffect(() => {
		function handleBeforeUnload() {
			if (!currentUser?.id) return
			const isExcludedAuthPath = isAuthExcludedPathname(
				location.pathname,
				excludedAuthPaths,
			)
			if (isExcludedAuthPath) return
			if (location.pathname.includes('/plan-block') || isPlanRestricted) return

			if (
				needsCreateProfileRedirect &&
				!location.pathname.includes('/create-profile')
			) {
				navigate('/create-profile', { replace: true })
				return
			}
		}
		handleBeforeUnload()
	}, [
		navigate,
		location.pathname,
		isPlanRestricted,
		currentUser,
		needsCreateProfileRedirect,
	])

	// Load Zoho IM widget dynamically from env
	useEffect(() => {
		const widgetUrl =
			process.env.NEXT_PUBLIC_ZOHO_IM_WIDGET_URL ||
			process.env.REACT_APP_ZOHO_IM_WIDGET_URL
		if (!widgetUrl) {
			return
		}

		// Check if script already exists
		if (document.getElementById('zohoimscript')) return

		if (!window.ZOHOIM) {
			const zohoIMInitializer = (a: string, b: unknown) => {
				;(zohoIMInitializer as unknown as Record<string, unknown>)[a] = b
			}
			window.ZOHOIM = zohoIMInitializer as NonNullable<Window['ZOHOIM']>
		}

		if (window.currentUser) {
			window.ZOHOIM.visitor = {
				name: window.currentUser.name || 'User',
				email: window.currentUser.email || '',
			}
		}

		// Create and append script
		const script = document.createElement('script')
		script.id = 'zohoimscript'
		script.type = 'text/javascript'
		script.src = widgetUrl
		script.defer = true
		document.head.appendChild(script)

		// Cleanup on unmount
		return () => {
			const existingScript = document.getElementById('zohoimscript')
			if (existingScript) {
				existingScript.remove()
			}
		}
	}, [])

	// const checkEmail = useCallback(() => {
	// 	const domain = isAuthenticated ? user?.email?.split('@')[1] : ''
	// 	if (domain && personnalDomains.includes(domain)) return 'personnal'
	// 	return 'professional'
	// }, [isAuthenticated, user])

	// Memoize sidebar visibility
	const showSidebar = useMemo(
		() =>
			assetsLocations.some((path) =>
				location.pathname.toLowerCase().includes(path.toLowerCase()),
			) &&
			excludedSidebarPaths.every(
				(path) => !location.pathname.toLowerCase().includes(path.toLowerCase()),
			),
		[location.pathname],
	)

	const shouldShowSidebar = showSidebar && !isPlanRestricted

	const mainLayoutElement =
		isAuthenticated && isPlanRestricted ? (
			<Navigate to="/plan-block" replace />
		) : (
			<MainLayout showSidebar={shouldShowSidebar} />
		)
	const homeEntryPaths = ['/', 'home']

	if (shouldShowUserLoadError) {
		return (
			<Fallback
				customError={
					isEmptyUserResponse ? 'Empty user object returned.' : undefined
				}
				error={userProfileError}
				prefix="Unable to load user profile"
			/>
		)
	}

	if (isLoading || shouldWaitForCurrentUser) {
		return <LockerLoader visible={isLoading || shouldWaitForCurrentUser} />
	}

	if (
		isAuthenticated ||
		isAuthExcludedPathname(location.pathname, excludedAuthPaths)
	) {
		return (
			<>
				{isAuthenticated && <AutoLogout />}
				<Box
					className={cn(
						'flex row h-[100vh] w-[100vw] overflow-auto',
						darkMode ? 'dark' : '',
					)}
				>
					<BillingProvider currentUser={currentUser}>
						{shouldShowSidebar && currentUser && (
							<Sidebar currentUser={currentUser} />
						)}
						<div
							id="top"
							className="app main-wrapper overflow-auto flex-1 dark:bg-background-dark"
						>
							<Routes>
								{/* public */}
								<Route path="/file/view" element={<PublicFileViewPage />} />
								<Route path="/esign/sign" element={<PublicSignFilePage />} />

								<Route path="/domain-verified" element={<DomainVerified />} />
								<Route
									path="/domain-verification-failed"
									element={<DomainVerificationFailed />}
								/>

								<Route path="/verify-email" element={<EmailVerification />} />
								<Route path="/email-verified" element={<EmailVerified />} />
								<Route path="/invalid-invite" element={<InvalidLink />} />
								<Route path="/accept-invite" element={<AcceptInvitePage />} />
								<Route path="/invite-accepted" element={<PasswordSuccess />} />
								<Route path="/mfa-choice" element={<MfaChoice />} />
								<Route path="/ad-login" element={<AdLogin />} />
								<Route path="/auth0-login" element={<AuthLogin />} />
								<Route path="/login" element={<AuthLogin />} />
								<Route
									path="/password-change-success"
									element={<PasswordSuccess />}
								/>
								<Route path="/maintenance" element={<Maintenance />} />
								<Route path="/user-blocked" element={<UserBlocked />} />
								<Route element={<AuthLayout />}>
									<Route path="preview" element={<FilePreviewPage />} />
									<Route path="plan-block" element={<PlanBlock />} />
									<Route element={mainLayoutElement}>
										<Route
											path="/create-profile"
											element={<PostRegisterPage />}
										/>
										{homeEntryPaths.map((path) => (
											<Route
												key={path}
												path={path}
												element={
													needsCreateProfileRedirect ? (
														<Navigate to="/create-profile" replace />
													) : (
														<HomePage />
													)
												}
											/>
										))}
										<Route path="recent" element={<RecentPage />} />
										<Route path="favorites" element={<FavoritesPage />} />
										<Route
											path="esign-requests"
											element={<EsignRequestsPage />}
										/>
										<Route path="signing" element={<SigningPage />} />
										<Route
											path="trash"
											element={
												<FeatureGuard requiredFeature={Features.Bin}>
													<TrashPage />
												</FeatureGuard>
											}
										/>
										<Route
											path="search-results"
											element={
												<FeatureGuard requiredFeature={Features.Nlp}>
													<SearchResultsPage />
												</FeatureGuard>
											}
										/>
										<Route
											path="/file-agent/auth/new"
											element={<AgentAuth />}
										/>
										<Route path="my-space">
											<Route
												path="notes"
												element={
													<FeatureGuard
														requiredFeature={Features.Collaboration}
													>
														<NotesPage />
													</FeatureGuard>
												}
											/>
											<Route
												path="notes/search-results"
												element={
													<FeatureGuard
														requiredFeature={Features.Collaboration}
													>
														<NotesPage />
													</FeatureGuard>
												}
											/>
											<Route
												path="contacts"
												element={
													<FeatureGuard
														requiredFeature={Features.Collaboration}
													>
														<ContactsPage />
													</FeatureGuard>
												}
											/>
											<Route
												path="boards"
												element={
													<FeatureGuard
														requiredFeature={Features.Collaboration}
													>
														<BoardsListPage />
													</FeatureGuard>
												}
											/>
											<Route
												path="boards/:id"
												element={
													<FeatureGuard
														requiredFeature={Features.Collaboration}
													>
														<BoardPage />
													</FeatureGuard>
												}
											/>
											<Route
												path="calendar"
												element={
													<FeatureGuard
														requiredFeature={Features.Collaboration}
													>
														<CalendarPage />
													</FeatureGuard>
												}
											/>
											<Route
												path="wikipages"
												element={
													<FeatureGuard
														requiredFeature={Features.Collaboration}
													>
														<WikiPages />
													</FeatureGuard>
												}
											/>
											<Route
												path="wikipages/:pageId"
												element={
													<FeatureGuard
														requiredFeature={Features.Collaboration}
													>
														<WikiPages />
													</FeatureGuard>
												}
											/>
											<Route
												path="inbox"
												element={
													<FeatureGuard
														requiredFeature={Features.Collaboration}
													>
														<InboxPage />
													</FeatureGuard>
												}
											/>
										</Route>
										<Route
											path="admin"
											element={
												<FeatureGuard requiredFeature={Features.ManageUsers}>
													<AdminLayout />
												</FeatureGuard>
											}
										>
											<Route
												path="users"
												element={
													<FeatureGuard requiredFeature={Features.ManageUsers}>
														<UsersPage />
													</FeatureGuard>
												}
											/>
											<Route
												path="groups"
												element={
													<FeatureGuard
														requiredFeature={
															Features.ManageGroupsRolesPermissions
														}
													>
														<GroupsPage />
													</FeatureGuard>
												}
											/>
											<Route
												path="roles"
												element={
													<FeatureGuard
														requiredFeature={
															Features.ManageGroupsRolesPermissions
														}
													>
														<RolesPage />
													</FeatureGuard>
												}
											/>
											<Route
												path="permissions"
												element={
													<FeatureGuard
														requiredFeature={
															Features.ManageGroupsRolesPermissions
														}
													>
														<PermissionsPage />
													</FeatureGuard>
												}
											/>
											<Route
												path="categories"
												element={
													<FeatureGuard
														requiredFeature={Features.AdvancedClassification}
													>
														<CategoriesPage />
													</FeatureGuard>
												}
											/>
											<Route
												path="audit"
												element={
													<FeatureGuard requiredFeature={Features.Audit}>
														<AuditPage />
													</FeatureGuard>
												}
											/>
										</Route>
										<Route path="settings">
											<Route path="user" element={<UserPage />} />
											<Route
												path="organization"
												element={<OrganizationPage />}
											/>
											<Route path="billing" element={<BillingPage />} />
										</Route>
										<Route
											path="file-agent-auth"
											element={
												<Navigate
													to={`/file-agent-auth.html${location.search}`}
													replace
												/>
											}
										/>
									</Route>
								</Route>
							</Routes>
						</div>
					</BillingProvider>
					<CustomSnackbar />
				</Box>
			</>
		)
	}
}

export default App
